<script setup>
/**
 * Página "Planes" (Victor 2026-06-07): catálogo showroom abierto desde el menú
 * lateral (entre Informes y Leads). Cada plan tiene "Ver" (detalle) y "Reservar"
 * (elige un cliente y abre el formulario de reserva precargado con el plan).
 */
import { ref } from 'vue';
import ContactAPI from 'dashboard/api/contacts';
import PlanesShowroomModal from 'dashboard/components/widgets/conversation/PlanesShowroomModal.vue';
import ReservarClienteModal from 'dashboard/components/widgets/conversation/ReservarClienteModal.vue';

const CAT_LABELS = {
  quince: 'Quinceañera', ninos: 'Niños/as', boda: 'Boda', embarazo: 'Embarazo',
  graduacion: 'Graduación', familiar: 'Familiar', empresarial: 'Empresarial',
  madre: 'Madre', recien_nacido: 'Recién Nacido', cumpleanos: 'Cumpleaños',
  mellizas: 'XV Mellizas', basico: 'Básico de Estudio', otro: 'Otro',
};
const catLabel = c => CAT_LABELS[c] || c || 'Otro';
const money = n => `RD$ ${(Number(n) || 0).toLocaleString('es-DO', { maximumFractionDigits: 0 })}`;

// Ver detalle del plan
const viewPlan = ref(null);
const detailFeatures = p => {
  const f = [];
  if (p.photos != null && p.photos !== '') f.push({ ic: '🖼', t: `${p.photos} fotografías editadas y retocadas` });
  if (p.minutes != null && p.minutes !== '') f.push({ ic: '🕐', t: `${p.minutes} minutos de sesión` });
  if (p.scenarios != null && p.scenarios !== '') f.push({ ic: '🖥', t: `${p.scenarios_up_to ? 'Hasta ' : ''}${p.scenarios} escenario${p.scenarios > 1 ? 's' : ''} a elegir` });
  if (p.outfits != null && p.outfits !== '') f.push({ ic: '👗', t: `${p.outfits} vestimenta${p.outfits > 1 ? 's' : ''}` });
  if (p.max_people != null && p.max_people !== '') f.push({ ic: '👥', t: `Máximo ${p.max_people} persona${p.max_people > 1 ? 's' : ''}` });
  return f;
};

// Reservar: elegir cliente
const reservePlan = ref(null);
const pickedContact = ref(null);
const query = ref('');
const results = ref([]);
const searching = ref(false);
let searchTimer = null;

const openReserve = plan => {
  reservePlan.value = plan;
  pickedContact.value = null;
  query.value = '';
  results.value = [];
};
const closePicker = () => {
  reservePlan.value = null;
  results.value = [];
};
const onQuery = () => {
  clearTimeout(searchTimer);
  const q = query.value.trim();
  if (!q) {
    results.value = [];
    return;
  }
  searchTimer = setTimeout(async () => {
    searching.value = true;
    try {
      const { data } = await ContactAPI.search(q);
      results.value = data?.payload || [];
    } catch (e) {
      results.value = [];
    } finally {
      searching.value = false;
    }
  }, 300);
};
const pickContact = c => {
  pickedContact.value = c;
};
const onReservarClose = () => {
  pickedContact.value = null;
  reservePlan.value = null;
};
const onReservarSaved = () => {
  // El modal de reserva muestra su propio estado; al guardar cerramos todo.
};
</script>

<template>
  <div class="planes-page">
    <PlanesShowroomModal :show="true" embedded @view="viewPlan = $event" @reserve="openReserve" />

    <!-- Ver: detalle del plan -->
    <teleport to="body">
      <div v-if="viewPlan" class="pd-overlay" @click.self="viewPlan = null">
        <div class="pd-modal">
          <div class="pd-head">
            <div class="pd-badge"><div class="l">PLAN</div><div class="n">{{ viewPlan.name?.match(/\d+/)?.[0] || '★' }}</div></div>
            <div><div class="pd-title">{{ viewPlan.name }}</div><div class="pd-cat">{{ catLabel(viewPlan.category) }}</div></div>
            <button class="pd-x" @click="viewPlan = null">✕</button>
          </div>
          <div class="pd-body">
            <div v-for="(f, i) in detailFeatures(viewPlan)" :key="i" class="pd-f"><span class="pd-fic">{{ f.ic }}</span> {{ f.t }}</div>
            <div v-if="!detailFeatures(viewPlan).length" class="pd-empty">Este plan aún no tiene detalles cargados.</div>
            <div v-if="(viewPlan.payment_methods || []).length" class="pd-pays">
              <span v-for="m in viewPlan.payment_methods" :key="m" class="pd-pay">{{ m }}</span>
            </div>
          </div>
          <div class="pd-foot">
            <div class="pd-price"><small>RD$</small> {{ (Number(viewPlan.price) || 0).toLocaleString('es-DO', { maximumFractionDigits: 0 }) }}</div>
            <button class="pd-resv" @click="openReserve(viewPlan); viewPlan = null">📅 Reservar este plan</button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- Reservar: elegir cliente -->
    <teleport to="body">
      <div v-if="reservePlan && !pickedContact" class="pd-overlay" @click.self="closePicker">
        <div class="pd-modal">
          <div class="pd-head">
            <span class="pd-ic">📅</span>
            <div><div class="pd-title">Reservar · {{ reservePlan.name }}</div><div class="pd-cat">Elige el cliente · {{ money(reservePlan.price) }}</div></div>
            <button class="pd-x" @click="closePicker">✕</button>
          </div>
          <div class="pd-body">
            <input v-model="query" class="pd-search" type="text" placeholder="🔎 Buscar cliente por nombre o teléfono…" @input="onQuery" />
            <div v-if="searching" class="pd-empty">Buscando…</div>
            <div v-else-if="query && !results.length" class="pd-empty">Sin resultados para "{{ query }}".</div>
            <div v-else-if="!query" class="pd-empty">Escribe para buscar un cliente.</div>
            <div v-else class="pd-list">
              <div v-for="c in results" :key="c.id" class="pd-ci" @click="pickContact(c)">
                <div class="pd-av">{{ (c.name || '?').slice(0, 2).toUpperCase() }}</div>
                <div class="pd-ci-info"><div class="pd-cn">{{ c.name || 'Cliente' }}</div><div class="pd-cp">{{ c.phone_number || c.email || '—' }}</div></div>
                <span class="pd-go">›</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </teleport>

    <!-- Formulario de reserva con el plan precargado -->
    <ReservarClienteModal
      v-if="reservePlan && pickedContact"
      :contact-id="pickedContact.id"
      :contact-name="pickedContact.name"
      :phone="pickedContact.phone_number"
      :preset-plan-id="reservePlan.id"
      @close="onReservarClose"
      @saved="onReservarSaved"
    />
  </div>
</template>

<style lang="scss" scoped>
.planes-page { padding: 12px; height: 100%; overflow: hidden; }
.pd-overlay { position: fixed; inset: 0; z-index: 10001; background: rgba(0, 0, 0, 0.62); display: grid; place-items: center; padding: 20px; }
.pd-modal { width: 480px; max-width: 100%; max-height: 88vh; display: flex; flex-direction: column; background: linear-gradient(180deg, #141416, #0d0d0f); border: 1px solid rgba(201, 163, 90, 0.5); border-radius: 16px; overflow: hidden; box-shadow: 0 30px 80px rgba(0, 0, 0, 0.6); }
.pd-head { display: flex; align-items: center; gap: 11px; padding: 15px 18px; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
.pd-badge { display: flex; flex-direction: column; align-items: center; justify-content: center; width: 46px; height: 46px; border-radius: 11px; background: linear-gradient(160deg, #1b1f2a, #0e1016); border: 1px solid rgba(201, 163, 90, 0.45); flex: none; }
.pd-badge .l { font-size: 7px; color: #62626a; font-weight: 800; } .pd-badge .n { font-size: 19px; font-weight: 800; color: #dcc28d; line-height: 1; }
.pd-ic { width: 40px; height: 40px; border-radius: 11px; background: rgba(201, 163, 90, 0.12); border: 1px solid rgba(201, 163, 90, 0.5); display: grid; place-items: center; font-size: 17px; flex: none; }
.pd-title { font-size: 15px; font-weight: 800; color: #fff; }
.pd-cat { font-size: 11px; color: #c8843e; font-weight: 700; }
.pd-x { margin-left: auto; width: 30px; height: 30px; border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.08); background: none; color: #8b8b91; cursor: pointer; }
.pd-body { padding: 16px 18px; overflow-y: auto; display: flex; flex-direction: column; gap: 11px; }
.pd-f { display: flex; align-items: center; gap: 11px; font-size: 13px; color: #dcdce0; }
.pd-fic { width: 28px; height: 28px; border-radius: 8px; border: 1px solid rgba(201, 163, 90, 0.45); display: grid; place-items: center; font-size: 13px; color: #dcc28d; flex: none; }
.pd-empty { font-size: 12.5px; color: #8b8b91; text-align: center; padding: 16px; }
.pd-pays { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 4px; }
.pd-pay { font-size: 10px; font-weight: 700; color: #8b8b91; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 6px; padding: 3px 8px; }
.pd-foot { padding: 14px 18px; display: flex; align-items: center; gap: 12px; border-top: 1px solid rgba(255, 255, 255, 0.08); }
.pd-price { font-size: 20px; font-weight: 800; color: #dcc28d; }
.pd-price small { font-size: 12px; color: #8b8b91; font-weight: 600; }
.pd-resv { margin-left: auto; font-size: 13px; font-weight: 800; color: #11141c; background: linear-gradient(180deg, #dcc28d, #c9a35a); border: none; border-radius: 10px; padding: 11px 16px; cursor: pointer; }
.pd-search { width: 100%; background: #0c0c0e; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 11px 13px; font-size: 13px; color: #fff; outline: none; }
.pd-search:focus { border-color: rgba(201, 163, 90, 0.6); }
.pd-list { display: flex; flex-direction: column; gap: 4px; max-height: 320px; overflow-y: auto; }
.pd-ci { display: flex; align-items: center; gap: 11px; padding: 9px 10px; border-radius: 10px; cursor: pointer; }
.pd-ci:hover { background: rgba(255, 255, 255, 0.03); }
.pd-av { width: 34px; height: 34px; border-radius: 50%; background: linear-gradient(135deg, #242428, #151517); border: 1px solid rgba(255, 255, 255, 0.1); display: grid; place-items: center; font-size: 11px; font-weight: 800; color: #8b8b91; flex: none; }
.pd-cn { font-size: 13px; font-weight: 700; color: #fff; }
.pd-cp { font-size: 11px; color: #8b8b91; }
.pd-go { margin-left: auto; color: #62626a; font-size: 17px; }
.pd-ci:hover .pd-go { color: #dcc28d; }
</style>
